import { uniqBy } from 'lodash'
import defaultReducers from '@src/models/defaultReducers'
import { Notification, NotificationInboxStateFilter } from '@src/types'
import commonReducers from '../commonReducers'
import {
  DEFAULT_NOTIFICATION_INBOX_LIST_STATE,
  LiveNotificationRecord,
  NotificationRecord,
  NotificationsStoreState,
} from './types'

interface NotificationInboxPagePayload {
  stateFilter: NotificationInboxStateFilter
  records: NotificationRecord[]
  nextCursor: string | null
  append?: boolean
}

interface MarkNotificationsReadPayload {
  notificationIds: NotificationRecord['id'][]
  readAt: NonNullable<NotificationRecord['readAt']>
}

type NotificationWithLifecycleState = Pick<
  NotificationRecord,
  'id' | 'readAt' | 'dismissedAt' | 'completedAt'
>

const updateNotificationCollection = <
  T extends {
    id: NotificationRecord['id']
  }
>(
  notifications: T[],
  notification: Partial<T> & Pick<NotificationRecord, 'id'>,
) => {
  let hasUpdatedNotification = false

  const updatedNotifications = notifications.map((currentNotification) => {
    if (currentNotification.id !== notification.id) {
      return currentNotification
    }

    hasUpdatedNotification = true

    return {
      ...currentNotification,
      ...notification,
    }
  })

  return {
    hasUpdatedNotification,
    notifications: updatedNotifications,
  }
}

const updateLiveNotificationCollection = (
  notifications: LiveNotificationRecord[],
  notification: NotificationRecord,
) => {
  let hasUpdatedNotification = false

  const updatedNotifications = notifications.map((currentNotification) => {
    if (currentNotification.notification?.id !== notification.id) {
      return currentNotification
    }

    hasUpdatedNotification = true

    return {
      ...currentNotification,
      notification: {
        ...currentNotification.notification,
        ...notification,
      },
    }
  })

  return {
    hasUpdatedNotification,
    notifications: updatedNotifications,
  }
}

const findExistingNotification = (
  state: NotificationsStoreState,
  notificationId: NotificationRecord['id'],
) => {
  const existingNotification = state.notifications.find(
    (notification) => notification.id === notificationId,
  )

  if (existingNotification) {
    return existingNotification
  }

  const existingLiveNotification = state.liveNotifications.find(
    (notification) => notification.notification?.id === notificationId,
  )

  if (existingLiveNotification?.notification) {
    return existingLiveNotification.notification
  }

  return Object.values(state.inbox.notifications)
    .find((listState) =>
      listState.records.some((notification) => notification.id === notificationId),
    )
    ?.records.find((notification) => notification.id === notificationId)
}

const matchesStateFilter = (
  notification: NotificationWithLifecycleState,
  stateFilter: NotificationInboxStateFilter,
) => {
  switch (stateFilter) {
    case NotificationInboxStateFilter.All:
      return true
    case NotificationInboxStateFilter.Unread:
      return !notification.readAt
    case NotificationInboxStateFilter.Read:
      return !!notification.readAt && !notification.dismissedAt && !notification.completedAt
    case NotificationInboxStateFilter.Dismissed:
      return !!notification.dismissedAt
    case NotificationInboxStateFilter.Completed:
      return !!notification.completedAt
    default:
      return false
  }
}

const reconcileInboxNotifications = (
  notifications: NotificationsStoreState['inbox']['notifications'],
  notification: NotificationRecord,
) => {
  return Object.entries(notifications).reduce<NotificationsStoreState['inbox']['notifications']>(
    (nextNotifications, [stateFilterKey, listState]) => {
      const stateFilter = stateFilterKey as NotificationInboxStateFilter

      const updatedList = updateNotificationCollection(listState.records, notification)

      if (!updatedList.hasUpdatedNotification) {
        return {
          ...nextNotifications,
          [stateFilter]: listState,
        }
      }

      return {
        ...nextNotifications,
        [stateFilter]: {
          ...listState,
          records: updatedList.notifications.filter((record) =>
            matchesStateFilter(record, stateFilter),
          ),
        },
      }
    },
    {},
  )
}

export const reducers = {
  ...defaultReducers,
  fetchList: commonReducers.fetchList,
  markFetched: (state: NotificationsStoreState, { payload }: { payload: string }) => ({
    ...state,
    lastFetchedAt: payload,
  }),
  setLiveNotifications: (
    state: NotificationsStoreState,
    { payload }: { payload: LiveNotificationRecord[] },
  ) => ({
    ...state,
    liveNotifications: payload,
  }),
  setNotifications: (state: NotificationsStoreState, { payload }: { payload: Notification[] }) => ({
    ...state,
    notifications: payload,
  }),
  setInboxUnreadCount: (
    state: NotificationsStoreState,
    { payload: { unreadCount } }: { payload: { unreadCount: number } },
  ) => ({
    ...state,
    inbox: {
      ...state.inbox,
      unreadCount,
    },
  }),
  storeInboxPage: (
    state: NotificationsStoreState,
    { payload }: { payload: NotificationInboxPagePayload },
  ) => {
    const currentListState =
      state.inbox.notifications[payload.stateFilter] || DEFAULT_NOTIFICATION_INBOX_LIST_STATE

    return {
      ...state,
      inbox: {
        ...state.inbox,
        notifications: {
          ...state.inbox.notifications,
          [payload.stateFilter]: {
            records: payload.append
              ? uniqBy([...currentListState.records, ...payload.records], 'id')
              : payload.records,
            nextCursor: payload.nextCursor,
          },
        },
      },
    }
  },
  updateNotificationRecord: (
    state: NotificationsStoreState,
    { payload }: { payload: NotificationRecord },
  ) => {
    const previousNotification = findExistingNotification(state, payload.id)
    const updatedNotifications = updateNotificationCollection(state.notifications, payload)
    const updatedLiveNotifications = updateLiveNotificationCollection(
      state.liveNotifications,
      payload,
    )

    const wasUnread = previousNotification
      ? matchesStateFilter(previousNotification, NotificationInboxStateFilter.Unread)
      : false
    const isUnread = matchesStateFilter(payload, NotificationInboxStateFilter.Unread)

    return {
      ...state,
      notifications: updatedNotifications.notifications,
      liveNotifications: updatedLiveNotifications.notifications,
      inbox: {
        ...state.inbox,
        unreadCount:
          wasUnread && !isUnread
            ? Math.max(state.inbox.unreadCount - 1, 0)
            : state.inbox.unreadCount,
        notifications: reconcileInboxNotifications(state.inbox.notifications, payload),
      },
    }
  },
  markNotificationsRead: (
    state: NotificationsStoreState,
    { payload }: { payload: MarkNotificationsReadPayload },
  ) => {
    const { notificationIds, readAt } = payload
    const notificationIdsSet = new Set(notificationIds)
    let unreadTransitionCount = 0

    // Apply the same read transition across inbox + legacy/live collections.
    const updateRecordIfMatched = <
      T extends { id: NotificationRecord['id']; readAt?: string | null }
    >(
      record: T,
    ) => {
      if (!notificationIdsSet.has(record.id) || record.readAt) {
        return record
      }

      unreadTransitionCount += 1
      return {
        ...record,
        readAt,
      }
    }

    return {
      ...state,
      notifications: state.notifications.map(updateRecordIfMatched),
      liveNotifications: state.liveNotifications.map((liveNotification) => {
        if (!liveNotification.notification) {
          return liveNotification
        }

        const nextNotification = updateRecordIfMatched(liveNotification.notification)

        if (nextNotification === liveNotification.notification) {
          return liveNotification
        }

        return {
          ...liveNotification,
          notification: nextNotification,
        }
      }),
      inbox: {
        ...state.inbox,
        unreadCount: Math.max(state.inbox.unreadCount - unreadTransitionCount, 0),
        // Re-filter each cached inbox state to keep per-filter lists consistent.
        notifications: Object.entries(state.inbox.notifications).reduce<
          NotificationsStoreState['inbox']['notifications']
        >((nextNotifications, [stateFilterKey, listState]) => {
          const stateFilter = stateFilterKey as NotificationInboxStateFilter
          const nextRecords = listState.records
            .map(updateRecordIfMatched)
            .filter((record) => matchesStateFilter(record, stateFilter))

          return {
            ...nextNotifications,
            [stateFilter]: {
              ...listState,
              records: nextRecords,
            },
          }
        }, {}),
      },
    }
  },
  removeNotification: (
    state: NotificationsStoreState,
    { payload }: { payload: Pick<NotificationRecord, 'id'> },
  ) => ({
    ...state,
    notifications: state.notifications.filter((notification) => notification.id !== payload.id),
    liveNotifications: state.liveNotifications.filter(
      (notification) => notification.notification?.id !== payload.id,
    ),
  }),
  removeLiveNotification: (
    state: NotificationsStoreState,
    { payload }: { payload: Pick<LiveNotificationRecord, 'id'> },
  ) => ({
    ...state,
    liveNotifications: state.liveNotifications.filter(
      (liveNotification) => liveNotification.id !== payload.id,
    ),
  }),
}

export default reducers
